/**
 * DX API endpoint builders.
 *
 * Each entry is a small function that builds a URL path for one backend
 * call (images, videos, audios, templates, ad sizes, 3D assets, and so on).
 * Services and stores call these instead of writing URL strings by hand,
 * so every request path lives in one place.
 *
 * Most builders take a `businessProfileId` because most data is scoped to
 * a business profile. The `Core*` builders skip that id - they hit the
 * shared, profile-less versions of the same resources.
 */
export const dxApiEndpoints = {
  GetFilteredProductVariants: (
    businessProfileId: number,
    catalogId: number
  ): string =>
    `businessProfiles/${businessProfileId}/productCatalogs/${catalogId}/productVariants`,
  SearchProductVariants: (
    businessProfileId: number,
    catalogId: number
  ): string =>
    `businessProfiles/${businessProfileId}/productCatalogs/${catalogId}/productVariants/search`,
  GetProductParameters: (businessProfileId: number) =>
    `businessProfiles/${businessProfileId}/productParameters`,
  GetPaginatedCatalogs: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/productCatalogs`,
  GetImages: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/images`,
  UploadImage: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/images/file`,
  UploadImageByUrl: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/images/url`,
  GetCoreImages: (): string => `roimatic/images`,
  UploadCoreImage: (): string => `roimatic/images/file`,
  UploadCoreImageByUrl: (): string => `roimatic/images/Url`,
  DeleteImages: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/images/status`,
  UpdateImage: (businessProfileId: number, id: number | string): string =>
    `businessProfiles/${businessProfileId}/roimatic/images/${id}`,
  SearchMediaByNames: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/media/searchByNames`,
  SearchCoreMediaByNames: (): string => `roimatic/media/searchByNames`,
  GetVideos: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/videos`,
  UploadVideo: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/videos/file`,
  UploadVideoByUrl: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/videos/url`,
  GetCoreVideos: (): string => `roimatic/videos`,
  UploadCoreVideo: (): string => `roimatic/videos/file`,
  UploadCoreVideoByUrl: (): string => `roimatic/videos/Url`,
  DeleteVideos: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/videos/status`,
  UpdateVideo: (businessProfileId: number, id: number | string): string =>
    `businessProfiles/${businessProfileId}/roimatic/videos/${id}`,
  GetAudios: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/audios`,
  UploadAudio: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/audios/File`,
  UploadAudioByUrl: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/audios/Url`,
  GetCoreAudios: (): string => `roimatic/audios`,
  UploadCoreAudio: (): string => `roimatic/audios/File`,
  UploadCoreAudioByUrl: (): string => `roimatic/audios/Url`,
  DeleteAudios: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/audios/status`,
  UpdateAudio: (businessProfileId: number, id: number | string): string =>
    `businessProfiles/${businessProfileId}/roimatic/audios/${id}`,
  SaveTemplate: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates`,
  UpdateTemplate: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates`,
  GetTemplates: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates`,
  SearchTemplates: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates/search`,
  GetTemplate: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates/${id}`,
  UpdateTemplateStatus: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates/${id}/status`,
  GetTemplateFeed: (businessProfileId: number, templateId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates/${templateId}/feeds`,
  GetCoreTemplateFeed: (templateId: number): string =>
    `roimatic/creative-templates/${templateId}/feeds`,
  GetFeeds: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-feeds/search`,
  GetFeed: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-feeds/${id}`,
  GetCoreFeed: (id: number): string => `roimatic/creative-feeds/${id}`,
  SaveFeed: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-feeds`,
  UpdateFeed: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-feeds/${id}`,
  RenameFeed: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-feeds/${id}/rename`,
  SaveCoreFeed: (): string => `roimatic/creative-feeds`,
  UpdateCoreFeed: (id: number): string => `roimatic/creative-feeds/${id}`,
  SaveCoreTemplate: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates/core`,
  UpdateCoreTemplate: (): string => `roimatic/creative-templates`,
  GetCoreTemplates: (): string => `roimatic/creative-templates`,
  GetCoreTemplate: (id: number): string => `roimatic/creative-templates/${id}`,
  UpdateCoreTemplateStatus: (id: number): string =>
    `roimatic/creative-templates/${id}/status`,
  GetBpUsers: (): string => 'users',
  SaveCreativeElement: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/creative-elements`,
  GetCreativeElements: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/creative-elements`,
  GetCreativeElement: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/creative-elements/${id}`,
  GetCreativeElementMeta: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/creative-elements/meta`,
  UpdateCreativeElement: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/creative-elements/${id}`,
  DeleteCreativeElement: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/creative-elements/${id}`,
  RenameCreativeElement: (businessProfileId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/creative-elements/${id}/rename`,
  GetCreativeElementTags: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/creative-builder-element-tags`,
  CreateCreativeElementTag: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/creative-builder-element-tags`,
  SearchCreativeElementTags: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/creative-builder-element-tags/search`,
  GetBusinessProfileSettings: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/settings`,
  PublishTemplate: (businessProfileId: number, templateId: number): string =>
    `businessProfiles/${businessProfileId}/creative-templates/${templateId}/published`,
  PublishTemplateStatus: (
    businessProfileId: number,
    templateId: number
  ): string =>
    `businessProfiles/${businessProfileId}/creative-templates/${templateId}/published/status`,
  InvalidatePublishCache: (
    businessProfileId: number,
    templateId: number
  ): string =>
    `businessProfiles/${businessProfileId}/creative-templates/${templateId}/published/invalidate-cache`,
  GetGoogleFonts: (): string => `roimatic/googleFonts`,
  UploadFont: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/fonts/file`,
  UploadMultipleFonts: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/fonts/files`,
  GetFonts: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/fonts`,

  GetAdSizes: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/adSizes`,
  GetCoreAdSizes: (): string => `roimatic/adSizes`,
  CreateAdSize: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/adSizes`,
  DeleteAdSize: (businessProfileId: number, id: number | string): string =>
    `businessProfiles/${businessProfileId}/roimatic/adSizes/${id}`,
  Upload3DAsset: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/3dAssets/File`,
  Get3DAssets: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/3dAssets/search`,
  Get3DAssetCategories: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/3dAssets/categories`,
  GetCore3DAssets: (): string => `roimatic/3dAssets/search`,
  GetCore3DAssetCategories: (): string => `roimatic/3dAssets/categories`,
  TranslateText: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/Translations/translate`,
  GetLanguages: (): string => 'languages',
  CreateCreativeBuilderRenderRequest: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creativeBuilderRenderRequests`,
  GetCreativeBuilderRenderRequest: (
    businessProfileId: number,
    renderRequestId: number
  ): string =>
    `businessProfiles/${businessProfileId}/roimatic/creativeBuilderRenderRequests/${renderRequestId}`,
  SearchCreativeBuilderRenderRequests: (
    businessProfileId: number
  ): string => `businessProfiles/${businessProfileId}/roimatic/creativeBuilderRenderRequests/search`,
  GetTemplateLinkedFeed: (
    businessProfileId: number,
    templateId: number,
    feedId: number
  ): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates/${templateId}/feeds/${feedId}`,
  GetRootFolders: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creativeBuilderRenderRequests/folders/common`,
  GetAccounts: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/accounts`,
  GetGoogleDrivePickerAccessToken: (
    businessProfileId: number,
    searchEngineAuthorizationId: number
  ): string =>
    `businessProfiles/${businessProfileId}/searchEngineAuthorization/googledrive/${searchEngineAuthorizationId}/accessToken`,
  GetExportPresets: (businessProfileId: number, templateId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates/${templateId}/exportPresets`,
  ExportPreset: (businessProfileId: number, templateId: number, id: number): string =>
    `businessProfiles/${businessProfileId}/roimatic/creative-templates/${templateId}/exportPresets/${id}`,

  /**
   * AI Studio. The API version already sits in the client's base URL, so
   * these paths carry no `v1` segment.
   */
  AiStudioSessions: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/stunnrAiStudio/sessions`,
  AiStudioGenerations: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/stunnrAiStudio/generations`,
  /**
   * Marks one output deleted. A PATCH, not a DELETE: the history row is an
   * activity log and is never changed; the backend records the deletion in a
   * side table and every read excludes the output from then on. Keyed by the
   * activity log id, which history hands back with each generation.
   */
  AiStudioDeleteOutput: (
    businessProfileId: number,
    activityLogId: number,
    outputId: string
  ): string =>
    `businessProfiles/${businessProfileId}/stunnrAiStudio/activityLogs/${activityLogId}/outputs/${outputId}`,
  /** Image proxy, so a cross-origin generated image can be downloaded. */
  AdGenImages: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/adGenImages`,
  GetProductImageTagGroups: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/productImageTagGroups`,
  GetProductImageTagsByGroupId: (
    businessProfileId: number,
    tagGroupId: number
  ): string =>
    `businessProfiles/${businessProfileId}/productImageTagGroups/${tagGroupId}/productImageTags`,
  GetProductImageTags: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/productImageTags`,
  GetProductVariantImages: (
    businessProfileId: number,
    catalogId: number
  ): string =>
    `businessProfiles/${businessProfileId}/productCatalogs/${catalogId}/productImages`,
  GetRoimaticConfiguration: (businessProfileId: number): string =>
    `businessProfiles/${businessProfileId}/roimatic``businessProfiles/${businessProfileId}/roimatic/creative-templates/${templateId}/exportPresets/${id}`,
  getCurrentUser: (): string => 'users/me',
};
